// AGC: Game Guide component - Accordion style for mobile-friendly viewing
// AGC: Comprehensive game guide explaining all battle mechanics
import React, { useState, useEffect } from 'react'
import { ChevronDown, ChevronUp, ArrowLeft, BookOpen, Shield, Sword, Zap, Snowflake, Trophy, Users, Wallet, Clock } from 'lucide-react'
import { playSoundSafe } from '../utils/audio'

interface GameGuideProps {
  onBack: () => void
  onStartSimulation?: () => void
  onShortVersion?: () => void
}

// AGC: Accordion item component for expandable sections
interface AccordionItemProps {
  id: string
  title: string
  icon?: string
  children: React.ReactNode
  defaultOpen?: boolean
  isOpen: boolean
  onToggle: () => void
  onCollapse?: () => void
}

const AccordionItem: React.FC<AccordionItemProps> = ({ id, title, icon, children, isOpen, onToggle, onCollapse }) => {
  const toggle = () => {
    playSoundSafe('/sounds/click.mp3', 0.2)
    onToggle()
  }

  const handleCollapse = () => {
    playSoundSafe('/sounds/click.mp3', 0.2)
    onToggle()
    // AGC: Scroll to table of contents after collapsing
    if (onCollapse) {
      setTimeout(() => {
        onCollapse()
      }, 100)
    }
  }

  return (
    <div className="border-b border-slate-700/50" id={id}>
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-800/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {icon && <span className="text-xl">{icon}</span>}
          <h3 className="text-base font-semibold text-white">{title}</h3>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 text-sm text-slate-300 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
          {children}
          {/* AGC: Collapse button at bottom of section */}
          <div className="pt-2 border-t border-slate-700/50 mt-4">
            <button
              onClick={handleCollapse}
              className="w-full flex items-center justify-center gap-2 py-2 text-xs text-slate-400 hover:text-slate-300 transition-colors"
            >
              <ChevronUp className="w-4 h-4" />
              <span>Collapse section</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// AGC: Table of Contents component
const TableOfContents: React.FC<{ sections: Array<{ id: string; title: string; icon?: string }>; onNavigate: (id: string) => void; onShortVersion?: () => void }> = ({ sections, onNavigate, onShortVersion }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="bg-slate-800/50 rounded-lg p-4 mb-4 border border-slate-700">
      <button
        onClick={() => {
          playSoundSafe('/sounds/click.mp3', 0.2)
          setIsExpanded(!isExpanded)
        }}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-blue-400" />
          <h2 className="text-lg font-semibold text-white">Table of Contents</h2>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isExpanded && (
        <>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => {
                  playSoundSafe('/sounds/click.mp3', 0.2)
                  onNavigate(section.id)
                  setIsExpanded(false)
                }}
                className="text-left px-3 py-2 rounded-lg hover:bg-slate-700/50 transition-colors text-sm text-slate-300 flex items-center gap-2"
              >
                {section.icon && <span>{section.icon}</span>}
                <span>{section.title}</span>
              </button>
            ))}
          </div>
          {onShortVersion && (
            <div className="mt-4 pt-3 border-t border-slate-700/50">
              <button
                onClick={() => {
                  playSoundSafe('/sounds/click.mp3', 0.3)
                  onShortVersion()
                }}
                className="w-full px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all transform hover:scale-105 text-sm font-semibold text-white"
              >
                📄 Check Short Version
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * AGC: Comprehensive game guide explaining all battle mechanics
 * AGC: Includes elements, buffs, rarity, leveling, and damage calculations
 */
export const GameGuide: React.FC<GameGuideProps> = ({ onBack, onStartSimulation, onShortVersion }) => {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())

  // AGC: Scroll to table of contents on page load
  useEffect(() => {
    setTimeout(() => {
      const tocElement = document.getElementById('table-of-contents')
      if (tocElement) {
        tocElement.scrollIntoView({ behavior: 'instant', block: 'start' })
        // AGC: Small offset to account for header
        window.scrollBy(0, -20)
      } else {
        // AGC: Fallback to top if TOC not found
        window.scrollTo({ top: 0, behavior: 'instant' })
      }
    }, 100)
  }, [])

  // AGC: Scroll to table of contents function
  const scrollToTableOfContents = () => {
    const tocElement = document.getElementById('table-of-contents')
    if (tocElement) {
      tocElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setTimeout(() => {
        // AGC: Small offset to account for header
        window.scrollBy(0, -20)
      }, 300)
    }
  }

  // AGC: Navigate to section by ID
  const navigateToSection = (id: string) => {
    setOpenSections(new Set([id])) // AGC: Single-open accordion
    setTimeout(() => {
      const element = document.getElementById(id)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  // AGC: Toggle section (single-open accordion)
  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set<string>()
      // AGC: If section is already open, close it. Otherwise, open only this section
      if (!prev.has(id)) {
        next.add(id)
      }
      return next
    })
  }

  // AGC: Check if section is open
  const isSectionOpen = (id: string) => openSections.has(id)

  // AGC: All sections data
  const sections = [
    { id: 'rock-paper-scissors', title: 'Hero: Rock-Paper-Scissors Basics', icon: '✂️' },
    { id: 'elements', title: 'Elements System', icon: '🌊' },
    { id: 'buffs', title: 'Buff NFT System', icon: '🛡️' },
    { id: 'potions', title: 'Potion NFT System', icon: '🧪' },
    { id: 'rarity', title: 'Rarity System', icon: '💎' },
    { id: 'leveling', title: 'Leveling System', icon: '📈' },
    { id: 'damage', title: 'Damage Calculation', icon: '⚔️' },
    { id: 'economics', title: 'Battle Economics', icon: '💰' },
    { id: 'tournaments', title: 'Tournament System', icon: '🏆' },
    { id: 'tips', title: 'Final Tips for Success', icon: '🎯' },
  ]

  return (
    <main className="pt-0 pb-[88px] px-3 min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-4xl mx-auto">
        {/* AGC: Header with back button */}
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => {
              playSoundSafe('/sounds/click.mp3', 0.3)
              onBack()
            }}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* AGC: Table of Contents */}
        <div id="table-of-contents">
          <TableOfContents sections={sections} onNavigate={navigateToSection} onShortVersion={onShortVersion} />
        </div>

        {/* AGC: Accordion sections */}
        <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden">
          {/* AGC: Section 0 - Hero: Rock-Paper-Scissors Basics */}
          <AccordionItem
            id="rock-paper-scissors"
            title="Hero: Rock-Paper-Scissors Basics"
            icon="✂️"
            isOpen={isSectionOpen('rock-paper-scissors')}
            onToggle={() => toggleSection('rock-paper-scissors')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-200 mb-4">The Core Battle System</h3>
                <p className="text-gray-300 mb-4">
                  Origami Battles uses the classic Rock-Paper-Scissors mechanic as the foundation. Each turn, you choose one of three origami forms:
                </p>
                <div className="bg-gray-900/50 rounded-lg p-6 mb-4">
                  <div className="flex items-center justify-center flex-wrap gap-4 text-lg">
                    <span className="text-gray-300 font-bold">🗿 ROCK</span>
                    <span className="text-gray-500">→</span>
                    <span className="text-blue-400 font-bold">📄 PAPER</span>
                    <span className="text-gray-500">→</span>
                    <span className="text-red-400 font-bold">✂️ SCISSORS</span>
                    <span className="text-gray-500">→</span>
                    <span className="text-gray-300 font-bold">🗿 ROCK</span>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
                  <h4 className="font-semibold text-green-400 mb-2">✅ WIN</h4>
                  <p className="text-sm text-gray-300 mb-2">
                    When your origami beats the opponent's:
                  </p>
                  <ul className="space-y-1 text-sm text-gray-300">
                    <li>🗿 <strong>Rock</strong> beats ✂️ Scissors</li>
                    <li>📄 <strong>Paper</strong> beats 🗿 Rock</li>
                    <li>✂️ <strong>Scissors</strong> beats 📄 Paper</li>
                  </ul>
                  <p className="text-xs text-green-300 mt-2">
                    <strong>Result:</strong> You deal damage to the opponent
                  </p>
                </div>
                
                <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-4">
                  <h4 className="font-semibold text-amber-400 mb-2">⚖️ DRAW</h4>
                  <p className="text-sm text-gray-300 mb-2">
                    When both players choose the same origami:
                  </p>
                  <ul className="space-y-1 text-sm text-gray-300">
                    <li>🗿 Rock vs 🗿 Rock</li>
                    <li>📄 Paper vs 📄 Paper</li>
                    <li>✂️ Scissors vs ✂️ Scissors</li>
                  </ul>
                  <p className="text-xs text-amber-300 mt-2">
                    <strong>Result:</strong> Both players deal reduced damage
                  </p>
                </div>

                <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
                  <h4 className="font-semibold text-red-400 mb-2">❌ LOSE</h4>
                  <p className="text-sm text-gray-300 mb-2">
                    When your origami loses to the opponent's:
                  </p>
                  <ul className="space-y-1 text-sm text-gray-300">
                    <li>🗿 Rock loses to 📄 Paper</li>
                    <li>📄 Paper loses to ✂️ Scissors</li>
                    <li>✂️ Scissors loses to 🗿 Rock</li>
                  </ul>
                  <p className="text-xs text-red-300 mt-2">
                    <strong>Result:</strong> Opponent deals damage to you
                  </p>
                </div>
              </div>

              <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
                <p className="text-sm text-blue-300">
                  <strong>💡 Remember:</strong> The outcome of Rock-Paper-Scissors determines base damage. Elements add additional bonuses or penalties on top of this base damage.
                </p>
              </div>
            </div>
          </AccordionItem>

          {/* AGC: Section 1 - Elements */}
          <AccordionItem
            id="elements"
            title="Elements System"
            icon="🌊"
            isOpen={isSectionOpen('elements')}
            onToggle={() => toggleSection('elements')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-200 mb-4">Element Advantage Cycle</h3>
                <p className="text-gray-300 mb-4">
                  Elements add strategic depth to battles. Each origami can be paired with an element that affects damage:
                </p>
                <div className="bg-gray-900/50 rounded-lg p-6 mb-4">
                  <div className="flex items-center justify-center flex-wrap gap-4 text-lg">
                    <span className="text-blue-400 font-bold">💧 WATER</span>
                    <span className="text-gray-500">→</span>
                    <span className="text-red-400 font-bold">🔥 FIRE</span>
                    <span className="text-gray-500">→</span>
                    <span className="text-cyan-300 font-bold">❄️ ICE</span>
                    <span className="text-gray-500">→</span>
                    <span className="text-green-400 font-bold">💨 WIND</span>
                    <span className="text-gray-500">→</span>
                    <span className="text-blue-400 font-bold">💧 WATER</span>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
                  <h4 className="font-semibold text-green-400 mb-2">✅ WIN (+10 Attack)</h4>
                  <p className="text-sm text-gray-300 mb-2">
                    When your element beats the opponent's in the cycle:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li>💧 <strong>Water</strong> beats 🔥 Fire → +10 attack bonus</li>
                    <li>🔥 <strong>Fire</strong> beats ❄️ Ice → +10 attack bonus</li>
                    <li>❄️ <strong>Ice</strong> beats 💨 Wind → +10 attack bonus</li>
                    <li>💨 <strong>Wind</strong> beats 💧 Water → +10 attack bonus</li>
                  </ul>
                </div>
                
                <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
                  <h4 className="font-semibold text-red-400 mb-2">❌ LOSE (-5 Attack)</h4>
                  <p className="text-sm text-gray-300 mb-2">
                    When your element loses to the opponent's in the cycle:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li>🔥 <strong>Fire</strong> loses to 💧 Water → -5 attack penalty</li>
                    <li>❄️ <strong>Ice</strong> loses to 🔥 Fire → -5 attack penalty</li>
                    <li>💨 <strong>Wind</strong> loses to ❄️ Ice → -5 attack penalty</li>
                    <li>💧 <strong>Water</strong> loses to 💨 Wind → -5 attack penalty</li>
                  </ul>
                  <p className="text-xs text-red-300 mt-2">
                    <strong>Note:</strong> This penalty is deducted from your attack power, not from your energy/health!
                  </p>
                </div>
              </div>

              <div className="bg-amber-900/20 border border-amber-700 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-amber-400 mb-2">⚖️ DRAW (0 Bonus)</h4>
                <p className="text-sm text-amber-300 mb-2">
                  When both players choose the same origami (e.g., Rock vs Rock):
                </p>
                <p className="text-sm text-gray-300">
                  <strong>Result:</strong> No element bonus applied → 0 damage bonus
                </p>
              </div>

              <div className="bg-purple-900/20 border border-purple-700 rounded-lg p-4">
                <h4 className="font-semibold text-purple-400 mb-2">➖ NEUTRAL (-5 Attack Each)</h4>
                <p className="text-sm text-purple-300 mb-2">
                  When elements are not in the cycle (e.g., Fire vs Wind):
                </p>
                <div className="bg-purple-900/30 rounded p-3 mt-2">
                  <p className="text-sm text-gray-300 mb-2">
                    <strong>Example:</strong> 🔥 Fire vs 💨 Wind
                  </p>
                  <p className="text-xs text-gray-300">
                    Both players deduct <strong>-5 attack</strong> from their damage. Neither element has an advantage over the other.
                  </p>
                </div>
              </div>

              <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
                <p className="text-sm text-blue-300">
                  <strong>💡 Strategy Tip:</strong> Element advantage matters! A +10 attack bonus for the winner and -5 attack penalty for the loser creates a total 15-point swing. 
                  Always try to counter your opponent's element choice!
                </p>
              </div>
            </div>
          </AccordionItem>

          {/* AGC: Section 2 - Buffs */}
          <AccordionItem
            id="buffs"
            title="Buff System"
            icon="🛡️"
            isOpen={isSectionOpen('buffs')}
            onToggle={() => toggleSection('buffs')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-200 mb-4">How Buffs Work</h3>
                <p className="text-gray-300 mb-4">
                  Buffs are special NFTs that give you advantages in battle. You can equip up to 2 buffs per battle, 
                  and each has a chance to activate every turn based on RNG (Random Number Generation).
                </p>
              </div>

              {/* AGC: Buff activation chances */}
              <div className="bg-gray-900/50 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-purple-300 mb-4">📊 Activation Chances (New System V2)</h4>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-gray-300 mb-2"><strong>1 Buff Equipped (Buff-Specific Rates):</strong></p>
                    <div className="bg-purple-900/30 rounded p-3 space-y-1">
                      <p className="text-purple-200">• <strong>Frost:</strong> 30% chance per turn</p>
                      <p className="text-purple-200">• <strong>Shield:</strong> 40% chance per turn</p>
                      <p className="text-purple-200">• <strong>Pierce:</strong> 50% chance per turn</p>
                      <p className="text-purple-200">• <strong>Haste:</strong> 60% chance per turn</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-gray-300 mb-2"><strong>2 Buffs Equipped (Multiple Activation Possible!):</strong></p>
                    <div className="bg-purple-900/30 rounded p-3 space-y-2">
                      <p className="text-purple-200"><strong>Position 1:</strong> Frost: 20% | Shield: 40% | Pierce: 50% | Haste: 60%</p>
                      <p className="text-purple-200"><strong>If 1st fails → 2nd chances boosted:</strong></p>
                      <p className="text-green-400 text-xs ml-4">✅ Frost: 40% | Shield: 60% | Pierce: 80% | Haste: 90%</p>
                      <p className="text-purple-200"><strong>If 1st succeeds → 2nd chances reduced:</strong></p>
                      <p className="text-amber-400 text-xs ml-4">⚠️ Frost: 10% | Shield: 20% | Pierce: 25% | Haste: 30%</p>
                      <p className="text-cyan-300 text-xs mt-2 font-semibold">
                        🎉 BOTH buffs can activate in same turn! (Rare but powerful!)
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* AGC: Individual buff details */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {/* Shield */}
                <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/30 border border-blue-700 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Shield className="w-8 h-8 text-blue-400" />
                    <div>
                      <h4 className="font-bold text-blue-300 text-lg">Shield</h4>
                      <p className="text-xs text-blue-400">Defensive Buff</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-300"><strong>Effect:</strong> Reduces incoming damage by 25%</p>
                    <p className="text-gray-300"><strong>Usage:</strong> Unlimited activations per battle</p>
                    <p className="text-gray-300"><strong>Best For:</strong> Surviving high-damage opponents</p>
                    <p className="text-blue-300 text-xs mt-2">
                      💡 <em>Example: 40 damage → 30 damage (blocked 10)</em>
                    </p>
                  </div>
                </div>

                {/* Pierce */}
                <div className="bg-gradient-to-br from-red-900/30 to-red-800/30 border border-red-700 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Sword className="w-8 h-8 text-red-400" />
                    <div>
                      <h4 className="font-bold text-red-300 text-lg">Pierce</h4>
                      <p className="text-xs text-red-400">Offensive Buff</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-300"><strong>Effect:</strong> Boosts your attack power by 25%</p>
                    <p className="text-gray-300"><strong>Usage:</strong> Unlimited activations per battle</p>
                    <p className="text-gray-300"><strong>Best For:</strong> Amplifying your damage output</p>
                    <p className="text-red-300 text-xs mt-2">
                      💡 <em>Example: 40 base damage → 50 damage (+25% boost)</em>
                    </p>
                  </div>
                </div>

                {/* Haste */}
                <div className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/30 border border-yellow-700 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Zap className="w-8 h-8 text-yellow-400" />
                    <div>
                      <h4 className="font-bold text-yellow-300 text-lg">Haste</h4>
                      <p className="text-xs text-yellow-400">Offensive Buff</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-300"><strong>Effect:</strong> +5 flat damage bonus</p>
                    <p className="text-gray-300"><strong>Usage:</strong> Unlimited activations per battle</p>
                    <p className="text-gray-300"><strong>Best For:</strong> Consistent damage boost</p>
                    <p className="text-yellow-300 text-xs mt-2">
                      💡 <em>Example: 15 base damage → 20 damage (before defense)</em>
                    </p>
                  </div>
                </div>

                {/* Frost */}
                <div className="bg-gradient-to-br from-cyan-900/30 to-cyan-800/30 border border-cyan-700 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Snowflake className="w-8 h-8 text-cyan-400" />
                    <div>
                      <h4 className="font-bold text-cyan-300 text-lg">Frost ❄️</h4>
                      <p className="text-xs text-cyan-400">Ultimate Buff</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-300"><strong>Effect:</strong> Freezes opponent (0 damage dealt)</p>
                    <p className="text-cyan-300"><strong>Usage:</strong> ⚠️ ONE-TIME USE ONLY per battle</p>
                    <p className="text-gray-300"><strong>Best For:</strong> Game-changing clutch moments</p>
                    <p className="text-cyan-300 text-xs mt-2">
                      💡 <em>Most powerful buff! Once activated, it won't trigger again.</em>
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-900/20 border border-purple-700 rounded-lg p-4">
                <p className="text-sm text-purple-300">
                  <strong>⚠️ Important:</strong> Frost is the ONLY one-time use buff. Shield, Pierce, and Haste can activate 
                  multiple times throughout the battle!
                </p>
              </div>
            </div>
          </AccordionItem>

          {/* AGC: Section 3 - Potion NFT System */}
          <AccordionItem
            id="potions"
            title="Potion NFT System"
            icon="🧪"
            isOpen={isSectionOpen('potions')}
            onToggle={() => toggleSection('potions')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-200 mb-4">Potion NFTs</h3>
                <p className="text-gray-300 mb-4">
                  Potions are mintable NFTs that provide guaranteed effects in battle (unlike buffs which have RNG activation).
                  Each potion is minted with a random rarity tier that determines its effectiveness.
                </p>
              </div>

              {/* AGC: Activation info */}
              <div className="bg-purple-900/30 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-purple-300 mb-2">⚡ Activation System</h4>
                <p className="text-purple-200 text-sm mb-2">
                  Potions have <strong>no RNG activation</strong> - they provide guaranteed effects when used:
                </p>
                <ul className="text-purple-200 text-sm space-y-1 ml-4">
                  <li>• <strong>Guaranteed effect</strong> when used (except Double Strike requires winning the turn)</li>
                  <li>• No chance-based activation like buffs</li>
                  <li>• One-time use per potion</li>
                </ul>
              </div>

              {/* AGC: Minting and Rarity */}
              <div className="bg-gray-900/50 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-blue-300 mb-4">🎲 Minting & Rarity System</h4>
                <p className="text-gray-300 mb-4 text-sm">
                  When you mint a potion, it has chances to appear in one of 5 rarity tiers. Higher FarmProScore improves your chances for better rarities.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-white/10 border border-white rounded-lg p-4">
                    <h5 className="font-bold text-white mb-2">⚪ Common</h5>
                    <p className="text-gray-300 text-sm mb-2">Fallback tier (if all rolls fail)</p>
                    <p className="text-white text-sm font-semibold">Multiplier: 1.00×</p>
                  </div>
                  
                  <div className="bg-green-900/30 border border-green-500 rounded-lg p-4">
                    <h5 className="font-bold text-green-400 mb-2">🟢 Uncommon</h5>
                    <p className="text-gray-300 text-sm mb-2">Base: 70% → Target: 98% (with FarmPro)</p>
                    <p className="text-green-400 text-sm font-semibold">Multiplier: 1.10×</p>
                  </div>
                  
                  <div className="bg-blue-900/30 border border-blue-500 rounded-lg p-4">
                    <h5 className="font-bold text-blue-400 mb-2">🔵 Rare</h5>
                    <p className="text-gray-300 text-sm mb-2">Base: 30% → Target: 55% (with FarmPro)</p>
                    <p className="text-blue-400 text-sm font-semibold">Multiplier: 1.20×</p>
                  </div>
                  
                  <div className="bg-purple-900/30 border border-purple-500 rounded-lg p-4">
                    <h5 className="font-bold text-purple-400 mb-2">🟣 Epic</h5>
                    <p className="text-gray-300 text-sm mb-2">Base: 10% → Target: 20% (with FarmPro)</p>
                    <p className="text-purple-400 text-sm font-semibold">Multiplier: 1.50×</p>
                  </div>
                  
                  <div className="bg-orange-900/30 border border-orange-500 rounded-lg p-4 col-span-2">
                    <h5 className="font-bold text-orange-400 mb-2">🟠 Legendary</h5>
                    <p className="text-gray-300 text-sm mb-2">Base: 1% → Target: 3% (with FarmPro)</p>
                    <p className="text-orange-400 text-sm font-semibold">Multiplier: 2.00×</p>
                  </div>
                </div>

                <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
                  <p className="text-blue-300 text-sm">
                    <strong>💪 FarmProScore Boost:</strong> Your FarmProScore (0-1) improves rarity chances using linear interpolation.
                    Higher scores (from quest completion and staking) significantly increase your odds of getting Epic and Legendary potions!
                  </p>
                </div>
              </div>

              {/* AGC: Potion types */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
                  <h4 className="font-bold text-red-300 text-lg mb-3">❤️ Healing Potion</h4>
                  <p className="text-gray-300 text-sm mb-3">Restores energy instantly when used</p>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-400">⚪ Common: +10 energy</p>
                    <p className="text-green-400">🟢 Uncommon: +11 energy</p>
                    <p className="text-blue-400">🔵 Rare: +12 energy</p>
                    <p className="text-purple-400">🟣 Epic: +15 energy</p>
                    <p className="text-orange-400">🟠 Legendary: +20 energy</p>
                  </div>
                </div>

                <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
                  <h4 className="font-bold text-blue-300 text-lg mb-3">✨ Mana Potion</h4>
                  <p className="text-gray-300 text-sm mb-3">Boosts buff power for ALL remaining turns</p>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-400">⚪ Common: +5% buff power</p>
                    <p className="text-green-400">🟢 Uncommon: +5.5% buff power</p>
                    <p className="text-blue-400">🔵 Rare: +6% buff power</p>
                    <p className="text-purple-400">🟣 Epic: +7.5% buff power</p>
                    <p className="text-orange-400">🟠 Legendary: +10% buff power</p>
                  </div>
                </div>

                <div className="bg-purple-900/30 border border-purple-700 rounded-lg p-4">
                  <h4 className="font-bold text-purple-300 text-lg mb-3">⚡ Double Strike Potion</h4>
                  <p className="text-gray-300 text-sm mb-3">Increases damage ONLY when you win the turn</p>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-400">⚪ Common: +25% damage</p>
                    <p className="text-green-400">🟢 Uncommon: +27.5% damage</p>
                    <p className="text-blue-400">🔵 Rare: +30% damage</p>
                    <p className="text-purple-400">🟣 Epic: +37.5% damage</p>
                    <p className="text-orange-400">🟠 Legendary: +50% damage</p>
                  </div>
                  <p className="text-purple-300 text-xs mt-2">⚠️ Requires winning turn to activate</p>
                </div>

                <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
                  <h4 className="font-bold text-green-300 text-lg mb-3">☠️ Poison Potion</h4>
                  <p className="text-gray-300 text-sm mb-3">Deals damage over time (5 turns)</p>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-400">⚪ Common: 2.5 dmg/turn (12.5 total)</p>
                    <p className="text-green-400">🟢 Uncommon: 2.75 dmg/turn (13.75 total)</p>
                    <p className="text-blue-400">🔵 Rare: 3.0 dmg/turn (15 total)</p>
                    <p className="text-purple-400">🟣 Epic: 3.75 dmg/turn (18.75 total)</p>
                    <p className="text-orange-400">🟠 Legendary: 5.0 dmg/turn (25 total)</p>
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>

          {/* AGC: Section 4 - Rarity System */}
          <AccordionItem
            id="rarity"
            title="Rarity System"
            icon="💎"
            isOpen={isSectionOpen('rarity')}
            onToggle={() => toggleSection('rarity')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-200 mb-4">What is Rarity?</h3>
                <p className="text-gray-300 mb-4">
                  Every turn, your origami has a chance to manifest as a rare form, granting bonus damage. 
                  Rarity is determined by RNG (0-999,999) and adds to your attack power.
                </p>
              </div>

              <div className="space-y-4 mb-6">
                {/* Diamond */}
                <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border-2 border-cyan-500 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">💎</span>
                      <div>
                        <h4 className="font-bold text-cyan-300 text-lg">DIAMOND</h4>
                        <p className="text-xs text-cyan-400">Ultra Rare</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-cyan-300">+50</p>
                      <p className="text-xs text-gray-400">Damage Bonus</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                    <div>
                      <p className="text-gray-400">Drop Chance:</p>
                      <p className="text-cyan-300 font-semibold">0.1% (1 in 1,000)</p>
                    </div>
                    <div>
                      <p className="text-gray-400">RNG Range:</p>
                      <p className="text-cyan-300 font-mono text-xs">0 - 999</p>
                    </div>
                  </div>
                </div>

                {/* Gold */}
                <div className="bg-gradient-to-r from-yellow-900/30 to-amber-900/30 border-2 border-yellow-500 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">🥇</span>
                      <div>
                        <h4 className="font-bold text-yellow-300 text-lg">GOLD</h4>
                        <p className="text-xs text-yellow-400">Very Rare</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-yellow-300">+25</p>
                      <p className="text-xs text-gray-400">Damage Bonus</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                    <div>
                      <p className="text-gray-400">Drop Chance:</p>
                      <p className="text-yellow-300 font-semibold">1% (1 in 100)</p>
                    </div>
                    <div>
                      <p className="text-gray-400">RNG Range:</p>
                      <p className="text-yellow-300 font-mono text-xs">1,000 - 10,999</p>
                    </div>
                  </div>
                </div>

                {/* Silver */}
                <div className="bg-gradient-to-r from-gray-700/30 to-gray-600/30 border-2 border-gray-400 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">🥈</span>
                      <div>
                        <h4 className="font-bold text-gray-300 text-lg">SILVER</h4>
                        <p className="text-xs text-gray-400">Rare</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-300">+10</p>
                      <p className="text-xs text-gray-400">Damage Bonus</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                    <div>
                      <p className="text-gray-400">Drop Chance:</p>
                      <p className="text-gray-300 font-semibold">5% (1 in 20)</p>
                    </div>
                    <div>
                      <p className="text-gray-400">RNG Range:</p>
                      <p className="text-gray-300 font-mono text-xs">11,000 - 60,999</p>
                    </div>
                  </div>
                </div>

                {/* Bronze */}
                <div className="bg-gradient-to-r from-orange-900/30 to-orange-800/30 border-2 border-orange-600 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">🥉</span>
                      <div>
                        <h4 className="font-bold text-orange-300 text-lg">BRONZE</h4>
                        <p className="text-xs text-orange-400">Uncommon</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-orange-300">+5</p>
                      <p className="text-xs text-gray-400">Damage Bonus</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                    <div>
                      <p className="text-gray-400">Drop Chance:</p>
                      <p className="text-orange-300 font-semibold">15.0% (1 in ~6.67)</p>
                    </div>
                    <div>
                      <p className="text-gray-400">RNG Range:</p>
                      <p className="text-orange-300 font-mono text-xs">61,000 - 160,999</p>
                    </div>
                  </div>
                </div>

                {/* None */}
                <div className="bg-gray-900/50 border border-gray-600 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">⚪</span>
                      <div>
                        <h4 className="font-bold text-gray-400 text-lg">NONE</h4>
                        <p className="text-xs text-gray-500">Common</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-400">+0</p>
                      <p className="text-xs text-gray-400">No Bonus</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                    <div>
                      <p className="text-gray-400">Drop Chance:</p>
                      <p className="text-gray-400 font-semibold">78.9% (most common)</p>
                    </div>
                    <div>
                      <p className="text-gray-400">RNG Range:</p>
                      <p className="text-gray-400 font-mono text-xs">161,000 - 999,999</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-amber-900/20 border border-amber-700 rounded-lg p-4">
                <p className="text-sm text-amber-300">
                  <strong>🎲 RNG Explained:</strong> Each turn, a random number from 0-999,999 is generated. 
                  This number determines your rarity. Lower numbers = rarer forms = more damage!
                </p>
              </div>
            </div>
          </AccordionItem>

          {/* AGC: Section 4 - Leveling System */}
          <AccordionItem
            id="leveling"
            title="Leveling System"
            icon="📈"
            isOpen={isSectionOpen('leveling')}
            onToggle={() => toggleSection('leveling')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-200 mb-4">Leveling System</h3>
                <p className="text-gray-300 mb-4">
                  Gain experience and level up to increase your combat effectiveness:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">⚔️</span>
                    <div>
                      <span className="font-semibold text-gray-200">Attack Power:</span>
                      <span className="text-gray-400 ml-2">+1 per level (base damage)</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🛡️</span>
                    <div>
                      <span className="font-semibold text-gray-200">Defense:</span>
                      <span className="text-gray-400 ml-2">+0.5 per level (damage reduction)</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">💪</span>
                    <div>
                      <span className="font-semibold text-gray-200">Health:</span>
                      <span className="text-gray-400 ml-2">+10 per level (max energy)</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🎯</span>
                    <div>
                      <span className="font-semibold text-gray-200">Experience:</span>
                      <span className="text-gray-400 ml-2">Earned by winning battles and completing challenges</span>
                    </div>
                  </div>
                </div>

                {/* XP Calculation Details */}
                <div className="mt-6 pt-4 border-t border-gray-700">
                  <h5 className="text-lg font-bold text-yellow-400 mb-3">
                    💡 How XP is Calculated
                  </h5>
                  <p className="text-gray-300 mb-4">
                    Your XP reward depends on both the battle outcome and your opponent's level:
                  </p>

                  {/* Base XP */}
                  <div className="bg-gray-900/50 rounded-lg p-4 mb-4">
                    <h6 className="font-bold text-cyan-400 mb-2">Base XP by Outcome (PvP):</h6>
                    <ul className="list-disc list-inside text-gray-300 space-y-1 ml-2">
                      <li><strong className="text-green-400">Win:</strong> 50 XP</li>
                      <li><strong className="text-yellow-400">Draw:</strong> 25 XP</li>
                      <li><strong className="text-red-400">Loss:</strong> 15 XP</li>
                    </ul>
                    <p className="text-gray-400 text-xs mt-2">
                      ⚠️ <strong>PvE Battles:</strong> All XP rewards are <strong className="text-yellow-400">50% of PvP values</strong> (Win: 25 XP, Draw: 12-13 XP, Loss: 7-8 XP)
                    </p>
                  </div>

                  {/* Level Difference Multipliers */}
                  <div className="bg-gray-900/50 rounded-lg p-4 mb-4">
                    <h6 className="font-bold text-purple-400 mb-2">Level Difference Multipliers:</h6>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="text-green-400 font-bold">↑</span>
                        <div>
                          <strong className="text-gray-200">Fighting Higher Level Opponents:</strong>
                          <span className="text-gray-300 ml-2">+10% XP per level above you</span>
                          <p className="text-gray-400 text-xs mt-1 ml-4">
                            Example: Level 5 vs Level 8 = +30% bonus (base XP × 1.3)
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400 font-bold">=</span>
                        <div>
                          <strong className="text-gray-200">Same Level:</strong>
                          <span className="text-gray-300 ml-2">No multiplier (base XP × 1.0)</span>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-red-400 font-bold">↓</span>
                        <div>
                          <strong className="text-gray-200">Fighting Lower Level Opponents:</strong>
                          <span className="text-gray-300 ml-2">-5% XP per level below you (minimum 50% of base XP)</span>
                          <p className="text-gray-400 text-xs mt-1 ml-4">
                            Example: Level 8 vs Level 5 = -15% penalty (base XP × 0.85, min 50%)
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* XP Calculation Examples */}
                  <div className="bg-gray-900/50 rounded-lg p-4">
                    <h6 className="font-bold text-blue-400 mb-2">📊 XP Calculation Examples:</h6>
                    <div className="space-y-3">
                      <div className="bg-gray-800 rounded p-2">
                        <p className="text-gray-200 mb-1">
                          <strong>Level 5 Player</strong> <span className="text-green-400">WINS</span> vs <strong>Level 8 Opponent</strong>:
                        </p>
                        <p className="text-gray-300">
                          Base: 50 XP × 1.3 (3 levels above) = <span className="text-green-400 font-bold">65 XP</span>
                        </p>
                      </div>
                      <div className="bg-gray-800 rounded p-2">
                        <p className="text-gray-200 mb-1">
                          <strong>Level 8 Player</strong> <span className="text-green-400">WINS</span> vs <strong>Level 5 Opponent</strong>:
                        </p>
                        <p className="text-gray-300">
                          Base: 50 XP × 0.85 (3 levels below) = <span className="text-yellow-400 font-bold">42 XP</span>
                        </p>
                      </div>
                      <div className="bg-gray-800 rounded p-2">
                        <p className="text-gray-200 mb-1">
                          <strong>Level 10 Player</strong> <span className="text-red-400">LOSES</span> vs <strong>Level 12 Opponent</strong>:
                        </p>
                        <p className="text-gray-300">
                          Base: 15 XP × 1.2 (2 levels above) = <span className="text-yellow-400 font-bold">18 XP</span>
                        </p>
                      </div>
                      <div className="bg-gray-800 rounded p-2">
                        <p className="text-gray-200 mb-1">
                          <strong>Level 7 Player</strong> <span className="text-yellow-400">DRAWS</span> vs <strong>Level 7 Opponent</strong>:
                        </p>
                        <p className="text-gray-300">
                          Base: 25 XP × 1.0 (same level) = <span className="text-yellow-400 font-bold">25 XP</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-400 text-xs mt-4">
                    Higher levels provide significant advantages in battle - every level matters! Challenge higher-level opponents for maximum XP rewards! 💪
                  </p>
                </div>
              </div>
            </div>
          </AccordionItem>

          {/* AGC: Section 5 - Damage Calculation */}
          <AccordionItem
            id="damage"
            title="Damage Calculation"
            icon="⚔️"
            isOpen={isSectionOpen('damage')}
            onToggle={() => toggleSection('damage')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-200 mb-4">Damage Formula Breakdown</h3>
                <p className="text-gray-300 mb-4">
                  Understanding how damage is calculated helps you make strategic decisions. Here's the complete 
                  step-by-step process:
                </p>
              </div>

              {/* AGC: Step-by-step damage calculation */}
              <div className="space-y-4 mb-6">
                <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-blue-500">
                  <h4 className="font-semibold text-blue-300 mb-2">Step 1: Base Damage</h4>
                  <div className="text-sm text-gray-300 space-y-1">
                    <p>• <strong>WIN</strong> (Rock beats Scissors): 30 damage</p>
                    <p>• <strong>DRAW</strong> (Rock vs Rock): 15 damage</p>
                    <p>• <strong>LOSE</strong> (Rock loses to Paper): 10 damage</p>
                  </div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-green-500">
                  <h4 className="font-semibold text-green-300 mb-2">Step 2: Level Bonus</h4>
                  <div className="bg-gray-800 rounded p-3 font-mono text-sm mb-2">
                    <p className="text-green-400">Damage + AttackerLevel</p>
                  </div>
                  <p className="text-sm text-gray-300">Example: Base 30 damage at Level 10 → 30 + 10 = 40 damage</p>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-cyan-500">
                  <h4 className="font-semibold text-cyan-300 mb-2">Step 3: Element Advantage</h4>
                  <div className="text-sm text-gray-300 space-y-1">
                    <p>• <strong>Advantage</strong> (win cycle): +10 damage</p>
                    <p>• <strong>DRAW</strong> (same origami choice, e.g., Rock vs Rock): +0 damage</p>
                    <p>• <strong>Disadvantage/Neutral</strong> (lose cycle OR same element): -5 damage</p>
                  </div>
                  <div className="text-xs text-cyan-400 mt-2 space-y-1">
                    <p>💡 Same origami choice (DRAW) = 0 bonus</p>
                    <p>💡 Same element (e.g., both Fire) or non-cycle element (e.g., Fire vs Wind) = -5 penalty</p>
                  </div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-amber-500">
                  <h4 className="font-semibold text-amber-300 mb-2">Step 4: Rarity Bonus</h4>
                  <div className="text-sm text-gray-300 space-y-1">
                    <p>• <strong>Diamond</strong>: +50 damage</p>
                    <p>• <strong>Gold</strong>: +25 damage</p>
                    <p>• <strong>Silver</strong>: +10 damage</p>
                    <p>• <strong>Bronze</strong>: +5 damage</p>
                    <p>• <strong>None</strong>: +0 damage</p>
                  </div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-yellow-500">
                  <h4 className="font-semibold text-yellow-300 mb-2">Step 5: Haste Buff (if active)</h4>
                  <div className="text-sm text-gray-300">
                    <p>If Haste buff activates: +5 damage</p>
                  </div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-red-500">
                  <h4 className="font-semibold text-red-300 mb-2">Step 6: Pierce Buff (if active)</h4>
                  <div className="text-sm text-gray-300 space-y-1 mt-2">
                    <p>• If <strong>Pierce</strong> buff active: Multiply damage by 1.25 (+25% boost)</p>
                  </div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-orange-500">
                  <h4 className="font-semibold text-orange-300 mb-2">Step 7: Defense Calculation</h4>
                  <div className="bg-gray-800 rounded p-3 font-mono text-sm mb-2">
                    <p className="text-orange-400">DefMul = 1 - (DefenderLevel ÷ 200)</p>
                  </div>
                  <div className="text-sm text-gray-300 space-y-1 mt-2">
                    <p>• Multiply damage by defense multiplier (higher defender level = less damage taken)</p>
                    <p className="text-xs text-orange-300">Minimum multiplier = 0.50 (50% damage taken)</p>
                  </div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-cyan-500">
                  <h4 className="font-semibold text-cyan-300 mb-2">Step 8: Frost Check</h4>
                  <div className="text-sm text-gray-300">
                    <p>If <strong>Frost</strong> buff activates on opponent: Set their damage to 0</p>
                  </div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-blue-500">
                  <h4 className="font-semibold text-blue-300 mb-2">Step 9: Shield Buff</h4>
                  <div className="text-sm text-gray-300">
                    <p>If <strong>Shield</strong> buff activates: Reduce incoming damage by 25%</p>
                  </div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-purple-500">
                  <h4 className="font-semibold text-purple-300 mb-2">Step 10: Final Damage</h4>
                  <div className="text-sm text-gray-300">
                    <p>Round to nearest integer → Apply to opponent's Energy Points (EP)</p>
                  </div>
                </div>
              </div>

              {/* AGC: Complete example calculation */}
              <div className="bg-gradient-to-r from-red-900/30 to-orange-900/30 border border-red-700 rounded-lg p-6">
                <h4 className="font-semibold text-red-300 mb-4 text-lg">📝 Complete Example</h4>
                <div className="space-y-3 text-sm">
                  <p className="text-gray-200"><strong>Scenario:</strong></p>
                  <div className="bg-gray-900/50 rounded p-3 space-y-1 text-gray-300">
                    <p>• Player A: Level 5, Rock + Water, Haste & Pierce buffs activated</p>
                    <p>• Player B: Level 3, Scissors + Fire, Shield buff activated</p>
                    <p>• Rarity: Player A manifests a Diamond form (ultra lucky!)</p>
                  </div>

                  <p className="text-gray-200 mt-4"><strong>Calculation:</strong></p>
                  <div className="bg-gray-900/50 rounded p-3 space-y-2 text-gray-300 font-mono text-xs">
                    <p>1. Base: 30 (Rock beats Scissors)</p>
                    <p>2. Level: 30 + 5 = 35</p>
                    <p>3. Element: 35 + 10 = 45 (Water advantage vs Fire)</p>
                    <p>4. Rarity: 45 + 50 = 95 (Diamond bonus)</p>
                    <p>5. Haste: 95 + 5 = 100</p>
                    <p>6. Pierce: 100 × 1.25 = 125 (Pierce +25% boost)</p>
                    <p>7. Defense: 125 × 0.985 = 123.13 (Level 3 defender = 1.5% reduction)</p>
                    <p>8. Frost: Not active ⇒ 123.13</p>
                    <p>9. Shield: 123.13 × 0.75 = 92.34 (Shield blocks 25%)</p>
                    <p className="text-red-400 font-bold">10. Final: 92 damage dealt to Player B!</p>
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>

          {/* AGC: Section 6 - Battle Economics */}
          <AccordionItem
            id="economics"
            title="Battle Economics"
            icon="💰"
            isOpen={isSectionOpen('economics')}
            onToggle={() => toggleSection('economics')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-200 mb-4">Token System</h3>
                <p className="text-gray-300 mb-4">
                  Origami Battles uses OGB tokens for battle entry fees and rewards. Understanding the economics 
                  helps you manage your resources effectively.
                </p>
              </div>

              {/* AGC: Battle costs and rewards */}
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
                  <h4 className="font-semibold text-green-300 mb-3 text-lg">🏆 Victory</h4>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-300"><strong>Cost:</strong> 50 OGB</p>
                    <p className="text-gray-300"><strong>Reward:</strong> 85 OGB</p>
                    <p className="text-green-400 font-bold"><strong>Net:</strong> +35 OGB Profit! 🎉</p>
                    <p className="text-gray-400 text-xs mt-2">You get your 50 back + 35 from opponent (15 OGB burned)</p>
                  </div>
                </div>

                <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
                  <h4 className="font-semibold text-red-300 mb-3 text-lg">💀 Defeat</h4>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-300"><strong>Cost:</strong> 50 OGB</p>
                    <p className="text-gray-300"><strong>Reward:</strong> 0 OGB</p>
                    <p className="text-red-400 font-bold"><strong>Net:</strong> -50 OGB Loss</p>
                    <p className="text-gray-400 text-xs mt-2">Your 50 OGB goes to winner, 25 burned</p>
                  </div>
                </div>

                <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-4">
                  <h4 className="font-semibold text-amber-300 mb-3 text-lg">⚖️ Draw</h4>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-300"><strong>Cost:</strong> 50 OGB</p>
                    <p className="text-gray-300"><strong>Reward:</strong> 40 OGB</p>
                    <p className="text-amber-400 font-bold"><strong>Net:</strong> -10 OGB Loss</p>
                    <p className="text-gray-400 text-xs mt-2">You get 40 back, 20 OGB total burned (10 from each player)</p>
                  </div>
                </div>
              </div>

              {/* AGC: Token burning */}
              <div className="bg-gray-900/50 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-orange-300 mb-4">🔥 Token Burning Mechanics</h4>
                <div className="space-y-3 text-sm text-gray-300">
                  <p><strong>Win/Loss Battle:</strong> 15 OGB tokens are permanently burned (15% of 100 OGB pool)</p>
                  <p><strong>Draw Battle:</strong> 20 OGB tokens are permanently burned (20% of 100 OGB pool)</p>
                  <p className="text-orange-300 mt-2">
                    💡 <em>Token burning reduces total supply, increasing scarcity and value!</em>
                  </p>
                </div>
              </div>

              <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4">
                <p className="text-sm text-yellow-300">
                  <strong>💡 Strategy:</strong> Win battles to earn profit! Your winnings can be used to buy buffs 
                  from the marketplace or level up faster. Consistent wins create a positive feedback loop!
                </p>
              </div>
            </div>
          </AccordionItem>

          {/* AGC: Section 7 - Tournaments */}
          <AccordionItem
            id="tournaments"
            title="Tournament System"
            icon="🏆"
            isOpen={isSectionOpen('tournaments')}
            onToggle={() => toggleSection('tournaments')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-4">
              <p className="text-gray-300">
                Tournaments are competitive round-robin events where every player faces every other player. They automatically
                start when enough challengers join, and they reward the top three players with on-chain prizes.
              </p>

              {/* Quick facts */}
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
                  <div className="flex items-center gap-2 text-blue-300 font-semibold mb-2">
                    <Users className="w-5 h-5" /> Player Requirements
                  </div>
                  <ul className="text-sm text-gray-300 space-y-1">
                    <li>Minimum players: <strong>3</strong></li>
                    <li>Maximum players: <strong>10</strong></li>
                    <li>Format: <strong>Round-robin</strong></li>
                    <li>Status flow: <strong>WAITING → COUNTDOWN → RUNNING</strong></li>
                  </ul>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
                  <div className="flex items-center gap-2 text-green-300 font-semibold mb-2">
                    <Wallet className="w-5 h-5" /> Payments
                  </div>
                  <ul className="text-sm text-gray-300 space-y-1">
                    <li>Each battle requires <strong>50 OGB</strong></li>
                    <li>Both players must click <em>I'm Ready</em> to trigger payment</li>
                    <li>Winner receives 75 OGB, loser burns 25 OGB (same as PvP)</li>
                    <li>All transactions are <span className="text-blue-400">on-chain</span> & BaseScan-verifiable</li>
                  </ul>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
                  <div className="flex items-center gap-2 text-yellow-300 font-semibold mb-2">
                    <Clock className="w-5 h-5" /> Countdown Logic
                  </div>
                  <ul className="text-sm text-gray-300 space-y-1">
                    <li>Lobby auto-starts once min players ready</li>
                    <li>Max-player lobbies skip countdown immediately</li>
                    <li>Countdown re-checks every time a player readies up</li>
                    <li>Ready state saved to Firestore for reconnect safety</li>
                  </ul>
                </div>
              </div>

              {/* Payment flow */}
              <div className="bg-slate-900/60 rounded-lg p-5 border border-slate-600 mb-6">
                <h3 className="text-xl font-bold text-green-300 mb-4">💳 Battle Payment Flow</h3>
                <ol className="list-decimal list-inside text-sm text-gray-300 space-y-2 ml-2">
                  <li>Tournament match is created when both players click <strong>Play</strong>.</li>
                  <li>Backend locks the match and prompts both players to confirm readiness.</li>
                  <li>Each player pays 50 OGB entry (transaction confirmed on Base).</li>
                  <li>Match state switches to <strong>WAITING_FOR_PAYMENT</strong> → <strong>IN_PROGRESS</strong>.</li>
                  <li>Winner earns 75 OGB, loser's stake burns 25 OGB. Draws refund 25 OGB to both; 50 OGB burned.</li>
                </ol>
              </div>

              {/* Tie-breakers */}
              <div className="bg-slate-900/60 rounded-lg p-5 border border-slate-600 mb-6">
                <h3 className="text-xl font-bold text-purple-300 mb-4">🎯 Tie-Breaking Rules (in order)</h3>
                <div className="space-y-3 text-sm text-gray-300">
                  <div className="bg-slate-800 rounded p-3 border-l-4 border-blue-500">
                    <p><strong>1. Total Wins:</strong> Highest win count takes priority.</p>
                  </div>
                  <div className="bg-slate-800 rounded p-3 border-l-4 border-green-500">
                    <p><strong>2. Player Level:</strong> Higher level ranks higher for equal wins.</p>
                  </div>
                  <div className="bg-slate-800 rounded p-3 border-l-4 border-yellow-500">
                    <p><strong>3. Global Win Rate:</strong> Higher all-time percentage wins the tie.</p>
                  </div>
                  <div className="bg-slate-800 rounded p-3 border-l-4 border-orange-500">
                    <p><strong>4. Total Battles:</strong> More lifetime battles edge out ties.</p>
                  </div>
                </div>
              </div>

              {/* Rewards */}
              <div className="bg-slate-900/60 rounded-lg p-5 border border-slate-600 mb-6">
                <h3 className="text-xl font-bold text-amber-300 mb-4">🎁 Rewards & Medals</h3>
                <div className="grid md:grid-cols-3 gap-4 text-center">
                  <div className="bg-gradient-to-br from-yellow-900/30 to-amber-900/30 border border-yellow-600 rounded-lg p-4">
                    <div className="text-4xl mb-2">🥇</div>
                    <p className="font-semibold text-yellow-200">Champion</p>
                    <p className="text-sm text-gray-300">Top payout + leaderboard spotlight</p>
                  </div>
                  <div className="bg-gradient-to-br from-gray-700/30 to-gray-600/30 border border-gray-500 rounded-lg p-4">
                    <div className="text-4xl mb-2">🥈</div>
                    <p className="font-semibold text-gray-200">Runner-Up</p>
                    <p className="text-sm text-gray-300">Strong reward tier</p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-900/30 to-orange-800/30 border border-orange-600 rounded-lg p-4">
                    <div className="text-4xl mb-2">🥉</div>
                    <p className="font-semibold text-orange-200">Bronze</p>
                    <p className="text-sm text-gray-300">Solid prize + experience boost</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-3 text-center">
                  All rewards are finalized through the RoundRobinTournament contract and can be audited on BaseScan.
                </p>
              </div>

              {/* Strategy tips */}
              <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700 rounded-lg p-5">
                <h3 className="text-xl font-bold text-purple-200 mb-4">🧠 Tournament Strategy Tips</h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-300">
                  <div>
                    <h4 className="font-semibold text-purple-200 mb-2">Preparation</h4>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Stock enough OGB to cover every scheduled match.</li>
                      <li>Level up beforehand; level is the second tie-breaker.</li>
                      <li>Equip your strongest ERC-1155 buffs (Frost, Shield, etc.).</li>
                      <li>Practice PvP to raise your global win rate before entering.</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-200 mb-2">During the Event</h4>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Click <strong>I'm Ready</strong> quickly—lobbies start as soon as everyone is ready.</li>
                      <li>Adapt to opponent tendencies (element picks, buff usage, etc.).</li>
                      <li>Save Frost for must-win rounds; it remains one-time-use.</li>
                      <li>Track standings to know when defensive play secures a medal.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>

          {/* AGC: Section 8 - Final Tips */}
          <AccordionItem
            id="tips"
            title="Final Tips for Success"
            icon="🎯"
            isOpen={isSectionOpen('tips')}
            onToggle={() => toggleSection('tips')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-semibold text-purple-300 mb-2">🧠 Strategic Tips:</h4>
                  <ul className="space-y-1 text-gray-300 list-disc list-inside">
                    <li>Counter element advantages matter more than you think</li>
                    <li>Save Frost buff for critical moments (one-time use!)</li>
                    <li>Level up consistently to gain compound advantages</li>
                    <li>Understand RNG - even bad luck can be overcome with skill</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-cyan-300 mb-2">💎 Advanced Tactics:</h4>
                  <ul className="space-y-1 text-gray-300 list-disc list-inside">
                    <li>Pierce buff is great against high-level opponents</li>
                    <li>Shield buff provides consistent defense over time</li>
                    <li>Rarity procs can turn the tide of battle instantly</li>
                    <li>Win rate matters - aim for 60%+ to profit long-term</li>
                  </ul>
                </div>
              </div>
              
              {onStartSimulation && (
                <div className="pt-4 border-t border-slate-700">
                  <button
                    onClick={() => {
                      playSoundSafe('/sounds/click.mp3', 0.3)
                      onStartSimulation()
                    }}
                    className="w-full px-8 py-3 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 
                             rounded-lg font-semibold transition-all transform hover:scale-105"
                  >
                    🎮 Try Simulation Battle
                  </button>
                  <p className="text-gray-400 text-sm mt-2 text-center">
                    Test game mechanics without any blockchain interaction!
                  </p>
                </div>
              )}
            </div>
          </AccordionItem>
        </div>

        {/* AGC: Action button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => {
              playSoundSafe('/sounds/click.mp3', 0.3)
              onBack()
            }}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 
                     rounded-lg font-semibold transition-all transform hover:scale-105"
          >
            Got It! Let's Battle! ⚔️
          </button>
        </div>
      </div>
    </main>
  )
}
